import StyledButton from '@/components/styled/StyledButton';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import HorizontalLine from '@/components/ui/HorizontalLine';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import { useLocation, useNavigate } from 'react-router';
import { PATHS } from '@/constants/Paths';
import { buildSuppliersListSearch } from '@/utils/searchParams/buildSuppliersListSearch';
import {
  FormProvider,
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import StyledInput from '@/components/styled/StyledInput';
import MarginRateStyledInput from '@/components/styled/MarginRateStyledInput';
import PlusIcon from '@/assets/icons/plus.svg?react';
import {
  SupplierFormData,
  SupplierSchema,
} from '@/types/suppliers/SupplierFormData';
import { zodResolver } from '@hookform/resolvers/zod';
import StyledSelect from '@/components/styled/StyledSelect';
import { useState } from 'react';
import ContactForm from '@/components/suppliers/ContactForm';
import { useCreateSupplier, useUpdateSupplier } from '@/api/supplier.api';
import { SupplierStatus } from '@/types/suppliers/SupplierStatus';
import {
  COUNTRIES,
  COUNTRIES_DATA,
  CURRENCIES,
} from '../../../../../shared/CountriesData';
import clsx from 'clsx';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '@/components/styled/StyledFormHeaderWithBreadcrumb';
import StyledFormSection from '@/components/styled/StyledFormSection';
import StyledButtonLoader from '@/components/styled/StyledButtonLoader';
import { useToast } from '@/hooks/useToast';
import { useChangeLabelValueWithSecondaryValue } from '@/hooks/useChangeLabelValueWithSecondaryValue';
import { SUPPLIER_MARGIN_RATE_LABEL } from '@/constants/marginRateLabels';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

type SubmitFormType = (isDraft?: boolean) => SubmitHandler<SupplierFormData>;

const CreateSuppliersPage = () => {
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { mutateAsync: createSupplierData, isError: isCreateSupplierError } =
    useCreateSupplier();
  const { mutateAsync: updateSupplierData, isError: isUpdateSupplierError } =
    useUpdateSupplier();

  const navigate = useNavigate();
  const location = useLocation();
  const [supplierId, setSupplierId] = useState<string | null>(null);
  const [contactsIds, setContactsIds] = useState<string[]>([]);

  const submitError =
    isCreateSupplierError || isUpdateSupplierError
      ? t('suppliers.errorCorrection')
      : null;

  const methods = useForm<SupplierFormData>({
    resolver: zodResolver(SupplierSchema),
    mode: 'onChange',
    defaultValues: {
      address: undefined,
      contacts: [{ email: undefined, name: undefined, phoneNumber: undefined }],
      currency: undefined,
      marginRate: undefined,
      name: undefined,
      country: undefined,
      city: undefined,
      postalCode: undefined,
    },
    shouldUnregister: true,
  });

  const {
    control,
    handleSubmit,
    getValues,
    formState: { isValid },
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
    shouldUnregister: true,
  });

  const currentName = useWatch({ control, name: 'name' });

  const changeCountryWithCurrency = useChangeLabelValueWithSecondaryValue({
    control,
    primaryName: 'country',
    secondaryName: 'currency',
    retrieveSecondary: (primaryValue) => {
      const countryData = COUNTRIES_DATA.find(
        (data) => data.Pays === primaryValue.value,
      );
      const currency = CURRENCIES.find(
        (currency) => currency.label === countryData?.Devise,
      );
      return currency;
    },
  });

  const { toastAsync } = useToast();

  const submitForm: SubmitFormType =
    (isDraft = true) =>
    async (data) => {
      if (isValid) {
        const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
        const supplier = supplierId
          ? await toastAsync(
              updateSupplierData({
                supplierId,
                supplierData: {
                  address: data.address,
                  contacts: data.contacts.map((contact, index) => ({
                    ...contact,
                    id: contactsIds[index] ?? undefined,
                  })),
                  currency: data.currency.value,
                  marginRate: parsedMarginRate.success
                    ? parsedMarginRate.data
                    : undefined,
                  name: data.name,
                  country: data.country.value,
                  city: data.city,
                  postalCode: data.postalCode,
                  status: isDraft ? undefined : SupplierStatus.PUBLISHED,
                },
              }),
              { success: t('suppliers.updateSuccess') },
            )
          : await toastAsync(
              createSupplierData({
                address: data.address,
                contacts: data.contacts,
                currency: data.currency ? data.currency.value : undefined,
                marginRate: parsedMarginRate.success
                  ? parsedMarginRate.data
                  : undefined,
                name: data.name,
                country: data.country ? data.country.value : undefined,
                city: data.city,
                postalCode: data.postalCode,
                status: isDraft ? undefined : SupplierStatus.PUBLISHED,
              }),
              { success: t('suppliers.createSuccess') },
            );
        const supplierData = supplier.data;
        if (!supplierId) setSupplierId(supplierData?.id);
        if (supplierData.contacts) {
          setContactsIds(
            supplierData.contacts.map((contact: { id: string }) => contact.id),
          );
        }
      }
      if (!isDraft)
        navigate(
          PATHS.home.suppliers.list +
            buildSuppliersListSearch(location.state?.listSearchParams),
        );
    };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={currentName || t('suppliers.newSupplier')}
          breadcrumb={
            <StyledButton
              variant="link"
              title={t('suppliers.backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() => {
                submitForm(true)(getValues());
                navigate(
                  PATHS.home.suppliers.list +
                    buildSuppliersListSearch(location.state?.listSearchParams),
                );
              }}
              icon={<ArrowLeftIcon />}
            />
          }
        >
          <StyledButton
            title={t('suppliers.cancel')}
            onClick={() =>
              navigate(
                PATHS.home.suppliers.list +
                  buildSuppliersListSearch(location.state?.listSearchParams),
              )
            }
            variant="outlined"
            className="font-semibold gap-1 px-3 py-2"
          />
          <StyledButtonLoader
            title={t('suppliers.saveAsDraft')}
            disabled={!isValid}
            successTitle={t('suppliers.draftSaved')}
            onClick={() => submitForm(true)(getValues())}
            className="font-semibold gap-1 px-3 py-2"
            icon={<SaveIcon className="w-5 h-5" />}
          />
        </StyledFormHeaderWithBreadcrumb>
        {submitError && (
          <p className="p-6 text-sm leading-6 font-medium text-error-500">
            {submitError}
          </p>
        )}
        <HorizontalLine />
        <StyledFormSection sectionTitle={t('suppliers.requiredFields')}>
          <StyledInput
            control={control}
            name="name"
            title={t('suppliers.supplierName')}
            placeholder={t('suppliers.exampleName')}
          />
          <div className="flex flex-col md:flex-row gap-4">
            <StyledSelect
              options={COUNTRIES}
              control={control}
              name="country"
              title={t('suppliers.country')}
              placeholder={t('suppliers.selectCountry')}
              className="w-3/5"
              onChange={changeCountryWithCurrency}
            />
            <StyledSelect
              options={CURRENCIES}
              control={control}
              name="currency"
              title={t('suppliers.mainCurrency')}
              placeholder={t('suppliers.selectCurrency')}
              className="w-2/5"
            />
          </div>
        </StyledFormSection>
        <HorizontalLine />
        <StyledFormSection sectionTitle={t('suppliers.administrativeInfo')}>
          <StyledInput
            control={control}
            name="address"
            title={t('suppliers.address')}
            placeholder={t('suppliers.addAddress')}
          />
          <div className="flex flex-col md:flex-row gap-4">
            <StyledInput
              control={control}
              name="city"
              title={t('suppliers.city')}
              placeholder={t('suppliers.addCity')}
            />
            <StyledInput
              control={control}
              name="postalCode"
              type="number"
              title={t('suppliers.postalCode')}
              placeholder={t('suppliers.addPostalCode')}
              hideNumberFieldArrows
            />
          </div>
          {fields.map((field, index) => (
            <ContactForm
              key={field.id}
              control={control}
              index={index}
              onRemoveContact={() => remove(index)}
            />
          ))}
          <StyledButton
            title={t('suppliers.addContact')}
            className="text-brand-600 w-fit p-0"
            variant="link"
            icon={<PlusIcon className="w-5 h-5 fill-brand-600" />}
            onClick={() =>
              append({
                name: undefined,
                email: undefined,
                phoneNumber: undefined,
              })
            }
          />
        </StyledFormSection>
        <HorizontalLine />
        <StyledFormSection sectionTitle={t('suppliers.marginRate')}>
          <MarginRateStyledInput
            control={control}
            name="marginRate"
            title={getTranslatedConst(SUPPLIER_MARGIN_RATE_LABEL)}
            placeholder={t('suppliers.addRate')}
          />
        </StyledFormSection>
        <HorizontalLine />
        <div className="flex flex-row-reverse p-6">
          <StyledButton
            title={t('suppliers.publishSupplier')}
            className="font-semibold gap-1 px-3 py-2"
            onClick={handleSubmit(submitForm(false))}
            disabled={!isValid}
            icon={
              <PlusIcon
                className={clsx('w-5 h-5', {
                  'fill-white': isValid,
                  'fill-gray-400': !isValid,
                })}
              />
            }
          />
        </div>
      </StyledContainer>
    </FormProvider>
  );
};

export default CreateSuppliersPage;
